import { useEffect, useState, useCallback, useMemo } from "react";
import { opsConsoleApi } from "@/api";
import {
  RefreshCw, ArrowRightLeft, X, Filter,
  ChevronDown, Users, Plane, Shield, Activity, Info,
} from "lucide-react";
import { toast } from "sonner";
import { exportCSV, todayStr } from "@/utils/exportUtils";

// ─── Info Tooltip ─────────────────────────────────────────────────────────────
function InfoTooltip({ text, position = "top", width = "w-64" }) {
  const [show, setShow] = useState(false);
  const posMap = {
    top:         "bottom-full mb-2 left-1/2 -translate-x-1/2",
    bottom:      "top-full mt-2 left-1/2 -translate-x-1/2",
    "top-left":  "bottom-full mb-2 left-0",
    "top-right": "bottom-full mb-2 right-0",
    left:        "right-full mr-2 top-1/2 -translate-y-1/2",
    right:       "left-full ml-2 top-1/2 -translate-y-1/2",
  };
  return (
    <span className="relative inline-flex items-center"
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Info size={10} className="text-muted-foreground/30 hover:text-primary/60 cursor-help transition-colors shrink-0" />
      {show && (
        <span className={`absolute z-[9999] ${width} p-2.5 bg-popover border border-border rounded-sm shadow-2xl text-[11px] text-muted-foreground leading-relaxed whitespace-normal pointer-events-none ${posMap[position] || posMap.top}`}>
          {text}
        </span>
      )}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getCrewCategory(rank) {
  const r = (rank || "").toLowerCase();
  if (r.includes("captain")) return "captain";
  if (r.includes("first officer") || r.startsWith("fo ") || r === "fo") return "fo";
  return "cabin_crew";
}

const CREW_TYPE_META = {
  captain:    { label: "Captains",       short: "Capt.", color: "text-blue-400",   icon: Shield },
  fo:         { label: "First Officers", short: "FO",    color: "text-indigo-400", icon: Plane  },
  cabin_crew: { label: "Cabin Crew",     short: "CC",    color: "text-purple-400", icon: Users  },
};

const STATUS_STYLE = {
  flying:   { dot: "bg-green-500",  text: "text-green-400",  badge: "bg-green-500/10 border-green-500/30 text-green-400",  label: "Flying"     },
  standby:  { dot: "bg-amber-500",  text: "text-amber-400",  badge: "bg-amber-500/10 border-amber-500/30 text-amber-400",  label: "Standby"    },
  training: { dot: "bg-indigo-500", text: "text-indigo-400", badge: "bg-indigo-500/10 border-indigo-500/30 text-indigo-400",label: "Training"   },
  on_duty:  { dot: "bg-blue-500",   text: "text-blue-400",   badge: "bg-blue-500/10 border-blue-500/30 text-blue-400",   label: "On Duty"    },
  rest:     { dot: "bg-purple-500", text: "text-purple-400", badge: "bg-purple-500/10 border-purple-500/30 text-purple-400",label: "In Rest"    },
  off_duty: { dot: "bg-border",     text: "text-muted-foreground", badge: "bg-secondary/30 border-border text-muted-foreground", label: "Off Duty" },
  sick:     { dot: "bg-red-500",    text: "text-red-400",    badge: "bg-red-500/10 border-red-500/30 text-red-400",      label: "Sick / NIA" },
};

function FdpBar({ used, limit = 13 }) {
  const pct   = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">{used}h / {limit}h</span>
    </div>
  );
}

// ─── Swap Modal ───────────────────────────────────────────────────────────────
function SwapModal({ fromCrew, allCrew, onClose, onSwapDone }) {
  const [toCrew,  setToCrew]  = useState(null);
  const [flight,  setFlight]  = useState("");
  const [reason,  setReason]  = useState("");
  const [loading, setLoading] = useState(false);
  const available = allCrew.filter(c => c.available_for_swap && c.id !== fromCrew.id);

  async function handleSwap() {
    if (!toCrew || !flight.trim()) return;
    setLoading(true);
    try {
      const r = await opsConsoleApi.swapCrew({
        from_crew_id: fromCrew.id, to_crew_id: toCrew.id,
        flight_number: flight.trim().toUpperCase(), override_reason: reason,
      });
      if (r.data.warnings?.length > 0) r.data.warnings.forEach(w => toast.warning(w));
      toast.success(`Swap confirmed: ${r.data.from_crew} → ${r.data.to_crew} on ${r.data.flight}`);
      onSwapDone();
    } catch { toast.error("Swap failed. Check logs."); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="swap-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Crew Swap</div>
            <div className="text-sm font-black text-foreground mt-0.5">Replace {fromCrew.name}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-background/50 border border-border rounded-sm">
            <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
            <div>
              <div className="text-xs font-semibold text-foreground">{fromCrew.name}</div>
              <div className="text-[11px] text-muted-foreground">{fromCrew.rank} · {fromCrew.base} · FDP remaining: {fromCrew.fdp_remaining_h}h</div>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">Flight Number</label>
            <input value={flight} onChange={e => setFlight(e.target.value)} placeholder="e.g. QP1101"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary uppercase"
              data-testid="swap-flight-input" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">
              Select Replacement ({available.length} available)
            </label>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {available.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">No available crew at this base</div>}
              {available.map(c => (
                <button key={c.id} onClick={() => setToCrew(c)}
                  className={`w-full flex items-center justify-between p-3 border rounded-sm text-left transition-all ${toCrew?.id === c.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 bg-background/40"}`}
                  data-testid={`swap-target-${c.id}`}>
                  <div>
                    <div className="text-xs font-semibold text-foreground">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground">{c.rank} · Fatigue: {c.fatigue_score}/100</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-mono text-green-400">{c.fdp_remaining_h}h FDP left</div>
                    <div className={`text-[10px] ${STATUS_STYLE[c.ops_status]?.text}`}>{c.ops_status_label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">Override Reason (optional)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Operational necessity..."
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex justify-between pt-2 border-t border-border">
            <button onClick={onClose} className="px-3 py-2 border border-border text-xs text-muted-foreground rounded-sm hover:text-foreground transition-colors">Cancel</button>
            <button onClick={handleSwap} disabled={!toCrew || !flight.trim() || loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-40 transition-colors"
              data-testid="swap-confirm-btn">
              {loading ? <><RefreshCw size={11} className="animate-spin" /> Processing...</> : <><ArrowRightLeft size={11} /> Confirm Swap</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Crew Card ────────────────────────────────────────────────────────────────
function CrewCard({ crew, onSwap }) {
  const st  = STATUS_STYLE[crew.ops_status] || STATUS_STYLE.off_duty;
  const cat = CREW_TYPE_META[getCrewCategory(crew.rank)];
  return (
    <div className="bg-card border border-border rounded-sm p-3 hover:border-primary/30 transition-colors group"
      data-testid={`crew-card-${crew.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-xs font-bold text-foreground truncate">{crew.name}</div>
          <div className="text-[11px] text-muted-foreground">{crew.rank} · {crew.fleet}</div>
        </div>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 border rounded-sm shrink-0 ${st.badge}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${st.dot} mr-1`} />
          {crew.ops_status_label}
        </span>
      </div>
      {crew.current_flight && (
        <div className="flex items-center gap-1.5 text-[11px] text-green-400 mb-2">
          <Plane size={10} /> {crew.current_flight}
        </div>
      )}
      <FdpBar used={crew.fdp_used_h} limit={13} />
      <div className="flex items-center justify-between mt-2">
        <span className={`text-[10px] font-mono ${cat?.color || "text-muted-foreground"}`}>{crew.employee_id}</span>
        {crew.available_for_swap && (
          <button onClick={() => onSwap(crew)}
            className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 transition-all"
            data-testid={`swap-btn-${crew.id}`}>
            <ArrowRightLeft size={9} /> Swap
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Mini Sparkline ──────────────────────────────────────────────────────────
function MiniSparkline({ data, color = "#60a5fa" }) {
  if (!data || data.length < 2) return null;
  const W = 88, H = 30;
  const max = Math.max(...data.map(d => d.count), 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - 4 - (d.count / max) * (H - 8);
    return [x, y];
  });
  const polyline = pts.map(p => p.join(",")).join(" ");
  const area = `M${pts[0][0]},${H} ` + pts.map(p => `L${p[0]},${p[1]}`).join(" ") + ` L${pts[pts.length - 1][0]},${H} Z`;
  const lastPt = pts[pts.length - 1];
  const gradId = `sg-${color.replace("#", "")}`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible shrink-0">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <polyline points={polyline} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="2.5" fill={color} />
    </svg>
  );
}

// ─── Crew Type Dropdown ───────────────────────────────────────────────────────
function CrewTypeDropdown({ value, onChange, counts }) {
  const [open, setOpen] = useState(false);
  const options = [
    { key: "ALL",        label: "All Types",       count: counts.total     },
    { key: "captain",    label: "Captains (Capt.)", count: counts.captain   },
    { key: "fo",         label: "First Officers (FO)", count: counts.fo     },
    { key: "cabin_crew", label: "Cabin Crew (CC)",  count: counts.cabin_crew },
  ];
  const active = options.find(o => o.key === value) || options[0];

  return (
    <div className="relative" data-testid="crew-type-dropdown">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-sm text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors bg-card/60">
        <Users size={11} />
        <span className="font-semibold">{active.label}</span>
        <span className="text-[10px] text-muted-foreground/60">({active.count})</span>
        <ChevronDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-30 bg-card border border-border rounded-sm shadow-xl min-w-[210px]">
          {options.map(o => (
            <button key={o.key} onClick={() => { onChange(o.key); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-xs transition-colors hover:bg-secondary/40 ${value === o.key ? "text-primary bg-primary/5" : "text-muted-foreground"}`}
              data-testid={`crew-type-option-${o.key}`}>
              <span className="font-medium">{o.label}</span>
              <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-sm border ${value === o.key ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{o.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OpsConsole() {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [baseFilter,     setBaseFilter]     = useState("ALL");
  const [crewTypeFilter, setCrewTypeFilter] = useState("ALL");
  const [swapTarget,     setSwapTarget]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await opsConsoleApi.getStatus();
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── All crew across ALL bases (for swap modal) ────────────────────────────
  const allCrew = useMemo(() => data ? Object.values(data.board).flat() : [], [data]);

  // ── Crew filtered by selected base ───────────────────────────────────────
  const baseCrew = useMemo(() => {
    if (!data) return [];
    if (baseFilter === "ALL") return allCrew;
    return data.board[baseFilter] || [];
  }, [data, baseFilter, allCrew]);

  // ── Crew type counts for the selected base (used by dropdown) ────────────
  const crewTypeCounts = useMemo(() => ({
    total:      baseCrew.length,
    captain:    baseCrew.filter(c => getCrewCategory(c.rank) === "captain").length,
    fo:         baseCrew.filter(c => getCrewCategory(c.rank) === "fo").length,
    cabin_crew: baseCrew.filter(c => getCrewCategory(c.rank) === "cabin_crew").length,
  }), [baseCrew]);

  // ── Crew filtered by base + crew type (for display and KPIs) ─────────────
  const filteredCrew = useMemo(() => {
    if (crewTypeFilter === "ALL") return baseCrew;
    return baseCrew.filter(c => getCrewCategory(c.rank) === crewTypeFilter);
  }, [baseCrew, crewTypeFilter]);

  // ── Dynamic KPI summary based on current filters ─────────────────────────
  const displaySummary = useMemo(() => ({
    total:     filteredCrew.length,
    flying:    filteredCrew.filter(c => c.ops_status === "flying").length,
    standby:   filteredCrew.filter(c => c.ops_status === "standby").length,
    off_duty:  filteredCrew.filter(c => c.ops_status === "off_duty").length,
    sick:      filteredCrew.filter(c => c.ops_status === "sick").length,
    available: filteredCrew.filter(c => c.available_for_swap).length,
  }), [filteredCrew]);

  // ── Crew type breakdown KPIs (always from base-filtered, not type-filtered) ─
  const typeBreakdown = useMemo(() => {
    // Build per-type trend: filter shift_trends hourly by the currently selected base
    // If a base is selected we scale global trend proportionally by base share
    const trends = data?.shift_trends || {};
    const globalTotal = {
      captain:    (trends.captain?.total    || 0),
      fo:         (trends.fo?.total         || 0),
      cabin_crew: (trends.cabin_crew?.total || 0),
    };

    function scaledTrend(key) {
      const global = trends[key];
      if (!global || !global.hourly?.length) return null;
      if (baseFilter === "ALL") return global.hourly;
      const baseCount  = crewTypeCounts[key]  || 0;
      const totalCount = globalTotal[key]      || 1;
      const scale = baseCount / totalCount;
      return global.hourly.map(pt => ({ ...pt, count: Math.round(pt.count * scale) }));
    }

    return ["captain", "fo", "cabin_crew"].map(key => {
      const meta    = CREW_TYPE_META[key];
      const trend   = trends[key] || {};
      const hourly  = scaledTrend(key);
      const flying  = baseCrew.filter(c => getCrewCategory(c.rank) === key && c.ops_status === "flying").length;
      const prevFly = hourly?.length ? hourly[0].count : (trend.prev_flying || 0);
      const delta   = flying - prevFly;
      return {
        key,
        ...meta,
        total:      crewTypeCounts[key],
        flying,
        standby:    baseCrew.filter(c => getCrewCategory(c.rank) === key && c.ops_status === "standby").length,
        available:  baseCrew.filter(c => getCrewCategory(c.rank) === key && c.available_for_swap).length,
        hourly,
        delta,
        deltaLabel: delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "=",
        prevFlying: prevFly,
      };
    });
  }, [baseCrew, crewTypeCounts, data, baseFilter]);

  // ── Displayed bases + filtered crew per base ──────────────────────────────
  const displayedBases = useMemo(() => {
    if (!data) return [];
    return (data.bases || []).filter(b => baseFilter === "ALL" || b === baseFilter);
  }, [data, baseFilter]);

  function getBoardCrew(base) {
    const crew = data?.board[base] || [];
    if (crewTypeFilter === "ALL") return crew;
    return crew.filter(c => getCrewCategory(c.rank) === crewTypeFilter);
  }

  function exportStatusCSV() {
    exportCSV({
      headers: [
        { label: "Emp ID",       key: "employee_id"     },
        { label: "Name",         key: "name"            },
        { label: "Rank",         key: "rank"            },
        { label: "Base",         key: "base"            },
        { label: "Fleet",        key: "fleet"           },
        { label: "Status",       key: "ops_status_label" },
        { label: "FDP Used (h)", key: "fdp_used_h"      },
        { label: "FDP Rem (h)",  key: "fdp_remaining_h" },
        { label: "Fatigue",      key: "fatigue_score"   },
        { label: "Available?",   key: "avail_str"       },
      ],
      rows: filteredCrew.map(c => ({ ...c, avail_str: c.available_for_swap ? "YES" : "NO" })),
      filename: `ops-console-status-${todayStr()}.csv`,
    });
  }

  const baseLabel = baseFilter === "ALL" ? "All Bases" : baseFilter;

  return (
    <div className="p-6 space-y-5" data-testid="ops-console">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight">Ops Console</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Real-time crew status board · {displaySummary.total} crew · {displaySummary.flying} flying · {displaySummary.standby} standby
            {baseFilter !== "ALL" && <span className="text-primary font-semibold"> · {baseFilter}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportStatusCSV}
            className="flex items-center gap-1.5 px-3 py-2 border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 rounded-sm transition-colors"
            data-testid="ops-export-btn">
            Export CSV
          </button>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
            data-testid="ops-refresh-btn">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Status KPIs — dynamic by base + crew type filter */}
      <div className="space-y-2" data-testid="ops-kpi-section">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Status Summary — {baseLabel}{crewTypeFilter !== "ALL" ? ` · ${CREW_TYPE_META[crewTypeFilter]?.label}` : ""}
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {[
            {
              label: "Flying",     val: displaySummary.flying,    color: "text-green-400",
              info: "Crew currently in a live Flight Duty Period (FDP) on an active sector — includes airborne crew and those in active taxi/push-back phases.",
            },
            {
              label: "Standby",    val: displaySummary.standby,   color: "text-amber-400",
              info: "Crew on Airport Standby (ASB) or Home Standby (HSB). Available for immediate deployment within their remaining FDP window. ASB must report to gate within 30 minutes.",
            },
            {
              label: "Off Duty",   val: displaySummary.off_duty,  color: "text-muted-foreground",
              info: "Crew in mandatory rest or rostered off. Contacting off-duty crew requires overtime authorisation and DGCA minimum rest rule compliance verification before deployment.",
            },
            {
              label: "Sick / NIA", val: displaySummary.sick,      color: "text-red-400",
              info: "Crew marked Sick or Not In Agreement (NIA). Sick crew are medically unfit for duty. NIA indicates crew who have declined an assignment — both require immediate HR and Ops follow-up.",
            },
            {
              label: "Available",  val: displaySummary.available, color: "text-blue-400",
              info: "Crew eligible for swaps and last-minute deployments — standby crew with sufficient FDP headroom, or off-duty crew who have completed their mandatory rest period and are within legal FDP limits.",
            },
            {
              label: "Total",      val: displaySummary.total,     color: "text-foreground",
              info: "Total crew count within the selected base and crew type filter — the sum of all status categories shown.",
            },
          ].map(({ label, val, color, info }) => (
            <div key={label} className="bg-card border border-border rounded-sm p-3 text-center relative" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="absolute top-2 right-2">
                <InfoTooltip text={info} position="top-right" width="w-72" />
              </div>
              <div className={`text-xl font-black ${color}`}>{val}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Crew Type Breakdown row with sparklines */}
        <div className="grid grid-cols-3 gap-3">
          {typeBreakdown.map(t => {
            const Icon = t.icon;
            const isActive = crewTypeFilter === t.key;
            const sparkColor = t.key === "captain" ? "#60a5fa" : t.key === "fo" ? "#818cf8" : "#a78bfa";
            const deltaPositive = t.delta > 0;
            const deltaZero     = t.delta === 0;
            const deltaCls      = deltaPositive
              ? "text-green-400 bg-green-500/10 border-green-500/20"
              : deltaZero
              ? "text-muted-foreground bg-secondary/30 border-border"
              : "text-red-400 bg-red-500/10 border-red-500/20";

            const cardInfoMap = {
              captain:    "Command pilot (ATPL holder) responsible for safe flight operation as Pilot-in-Command. DGCA mandates Type Rating validity and line checks every 6 months. One Captain required per flight at minimum.",
              fo:         "Co-pilot (CPL/ATPL) operating alongside the Captain. Can act as PIC on specific approvals (ETOPS, supervised return). Requires identical recurrency checks as the Captain under DGCA regulations.",
              cabin_crew: "Cabin crew responsible for passenger safety, service, and emergency procedures. DGCA CAR Section 8 minimum: 1 per 50 seats. SEP (Safety Equipment & Procedures) must be current.",
            };
            const trendInfo   = "8-hour rolling flying count trend. Each data point represents the number of this crew type actively airborne at that hour. Use to spot shift transition gaps or unexpected depletion.";
            const availInfo   = "Crew eligible for an immediate crew swap — on standby with sufficient FDP headroom, or rested and within DGCA FDP limits. Click a crew card to initiate a swap.";
            const standbyInfo = "Crew on Airport or Home Standby, awaiting activation. Airport Standby (ASB) must report to gate within 30 min. Home Standby (HSB) typically within 2 hours.";
            const deltaInfo   = "Flying count delta vs 7 hours ago. Green (+) = more crew now airborne than 7h ago. Red (−) = fewer airborne — investigate if unexpected. Useful for shift handover situational awareness.";

            return (
              <button key={t.key} onClick={() => setCrewTypeFilter(isActive ? "ALL" : t.key)}
                className={`flex flex-col gap-2 p-3 border rounded-sm transition-all text-left group ${isActive ? "border-primary/50 bg-primary/5 shadow-sm" : "border-border bg-card hover:border-primary/30"}`}
                data-testid={`crew-type-card-${t.key}`}>
                {/* Top row: label + total + delta */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Icon size={12} className={t.color} />
                    <div>
                      <div className={`text-[11px] font-bold leading-none ${t.color} flex items-center gap-1`}>
                        {t.short}
                        <span onClick={e => e.stopPropagation()}>
                          <InfoTooltip text={cardInfoMap[t.key]} position="top-left" width="w-72" />
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground leading-none mt-0.5">{t.label}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xl font-black leading-none ${t.color}`}>{t.total}</div>
                    <span onClick={e => e.stopPropagation()} className="inline-flex items-center gap-0.5">
                      <span className={`inline-block mt-0.5 text-[9px] font-bold px-1 py-0.5 border rounded-sm leading-none ${deltaCls}`}>
                        {t.deltaLabel} vs 7h ago
                      </span>
                      <InfoTooltip text={deltaInfo} position="top-right" width="w-64" />
                    </span>
                  </div>
                </div>

                {/* Middle: sparkline */}
                <div className="flex items-end gap-2">
                  <MiniSparkline data={t.hourly} color={sparkColor} />
                  <div className="flex flex-col gap-0.5 pb-0.5 min-w-0">
                    <div className="flex items-center gap-1 text-[9px] text-muted-foreground leading-none" onClick={e => e.stopPropagation()}>
                      8h trend
                      <InfoTooltip text={trendInfo} position="top-left" width="w-72" />
                    </div>
                    <div className="text-[9px] text-muted-foreground leading-none">
                      <span className="text-green-400 font-bold">{t.prevFlying}</span> → <span className={`font-bold ${t.color}`}>{t.flying}</span> flying
                    </div>
                  </div>
                </div>

                {/* Bottom: stat pills */}
                <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
                  {t.standby > 0 && (
                    <span className="flex items-center gap-0.5">
                      <span className="text-amber-400">{t.standby} standby</span>
                      <span onClick={e => e.stopPropagation()}>
                        <InfoTooltip text={standbyInfo} position="bottom" width="w-64" />
                      </span>
                    </span>
                  )}
                  {t.standby > 0 && <span className="opacity-30">·</span>}
                  <span className="flex items-center gap-0.5">
                    {t.available} avail for swap
                    <span onClick={e => e.stopPropagation()}>
                      <InfoTooltip text={availInfo} position="bottom" width="w-64" />
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters row: Base + Crew Type */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Base filter buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={12} className="text-muted-foreground" />
          {["ALL", ...(data?.bases || [])].map(b => (
            <button key={b} onClick={() => { setBaseFilter(b); setCrewTypeFilter("ALL"); }}
              className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                baseFilter === b
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`base-filter-${b}`}>
              {b === "ALL" ? "All Bases" : b}
            </button>
          ))}
        </div>

        {/* Crew type dropdown */}
        <div className="ml-auto">
          <CrewTypeDropdown
            value={crewTypeFilter}
            onChange={setCrewTypeFilter}
            counts={crewTypeCounts}
          />
        </div>
      </div>

      {/* Crew Board */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <RefreshCw size={16} className="animate-spin" /> Loading crew status...
        </div>
      ) : (
        <div className="space-y-6">
          {displayedBases.map(base => {
            const crew = getBoardCrew(base);
            if (crew.length === 0) return null;
            return (
              <div key={base} data-testid={`base-section-${base}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-xs font-black uppercase tracking-widest text-primary">{base}</div>
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[10px] text-muted-foreground">{crew.length} crew</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {crew.map(c => (
                    <CrewCard key={c.id} crew={c} onSwap={setSwapTarget} />
                  ))}
                </div>
              </div>
            );
          })}
          {filteredCrew.length === 0 && !loading && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No crew found for the selected filters
            </div>
          )}
        </div>
      )}

      {/* Swap Modal */}
      {swapTarget && (
        <SwapModal
          fromCrew={swapTarget}
          allCrew={allCrew}
          onClose={() => setSwapTarget(null)}
          onSwapDone={() => { setSwapTarget(null); load(); }}
        />
      )}
    </div>
  );
}
